import { React, useEffect, useState, useCallback } from "react";
import AppWrapper from "../AppWrapper";
import { useSearchParams, useNavigate } from "react-router-dom";
import supabase from "../../supabase-client";
import {
  Page,
  Button,
  FormLayout,
  TextField,
  Select,
  BlockStack,
  Text,
  DropZone,
  InlineStack,
  Card,
} from "@shopify/polaris";
import { DeleteIcon, PlusIcon } from "@shopify/polaris-icons";
import { UserAuth } from "../../context/AuthContext";
import "./NewValidacion.css";

function NewValidacion() {
  const navigate = useNavigate();
  const { session } = UserAuth();
  const [searchParams] = useSearchParams();
  const editingId = searchParams.get("id");
  const [isLoading, setIsLoading] = useState(false);

  const [details, setDetails] = useState({
    first_names: "",
    father_last_name: "",
    mother_last_name: "",
    curp: "",
    rfc: "",
    email: "",
    story: "",
    car_category_id: "",
    car_segment_text: "",
    company_name: "",
    company_rfc: "",
    desired_from: "",
    desired_to: "",
  });

  const [phones, setPhones] = useState([""]);
  const [categories, setCategories] = useState([]);

  // File states per type
  const [ineF, setIneF] = useState([]);
  const [ineB, setIneB] = useState([]);
  const [licF, setLicF] = useState([]);
  const [licB, setLicB] = useState([]);
  const [otherFiles, setOtherFiles] = useState([]);

  // CURP duplicate check
  const [curpDuplicate, setCurpDuplicate] = useState(null);

  // Existing files when editing
  const [existingFiles, setExistingFiles] = useState([]);
  const [removedFileIds, setRemovedFileIds] = useState([]);

  // Store original values for change tracking
  const [originalDetails, setOriginalDetails] = useState(null);
  const [originalPhones, setOriginalPhones] = useState(null);

  useEffect(() => {
    const fetchCategories = async () => {
      const { data, error } = await supabase
        .from("car_categories")
        .select("id, name")
        .order("name");
      if (error) {
        console.error(error.message);
        return;
      }
      setCategories([
        { value: "", label: "Seleccionar" },
        ...data.map((c) => ({ value: String(c.id), label: c.name })),
      ]);
    };

    const fetchValidacion = async () => {
      const { data, error } = await supabase
        .from("validaciones")
        .select("*")
        .eq("id", editingId)
        .single();
      if (error) {
        console.error(error.message);
        return;
      }
      const parsed = {
        first_names: data.first_names || "",
        father_last_name: data.father_last_name || "",
        mother_last_name: data.mother_last_name || "",
        curp: data.curp || "",
        rfc: data.rfc || "",
        email: data.email || "",
        story: data.story || "",
        car_category_id: data.car_category_id ? String(data.car_category_id) : "",
        car_segment_text: data.car_segment_text || "",
        company_name: data.company_name || "",
        company_rfc: data.company_rfc || "",
        desired_from: data.desired_from || "",
        desired_to: data.desired_to || "",
      };
      const parsedPhones =
        data.phones && data.phones.length > 0 ? data.phones : [""];
      setDetails(parsed);
      setOriginalDetails({ ...parsed });
      setPhones(parsedPhones);
      setOriginalPhones([...parsedPhones]);

      // Fetch existing files
      const { data: files } = await supabase
        .from("validacion_files")
        .select("*")
        .eq("validacion_id", editingId);
      if (files) setExistingFiles(files);
    };

    fetchCategories();
    if (editingId) fetchValidacion();
  }, [editingId]);

  const handleChange = (value, id) => {
    if (id === "curp") {
      value = value.toUpperCase();
      if (curpDuplicate) setCurpDuplicate(null);
    }
    setDetails({ ...details, [id]: value });
  };

  const checkCurpDuplicate = async () => {
    const curp = details.curp?.trim();
    if (!curp) return;

    const { data } = await supabase
      .from("validaciones")
      .select("id, first_names, father_last_name")
      .eq("curp", curp)
      .neq("id", editingId || "")
      .limit(1);

    if (data && data.length > 0) {
      setCurpDuplicate(data[0]);
    } else {
      setCurpDuplicate(null);
    }
  };

  const handlePhoneChange = (value, index) => {
    const updated = [...phones];
    updated[index] = value;
    setPhones(updated);
  };

  const addPhone = () => setPhones([...phones, ""]);

  const removePhone = (index) => {
    if (phones.length <= 1) return;
    setPhones(phones.filter((_, i) => i !== index));
  };

  const handleIneFDrop = useCallback((_drop, accepted) => {
    setIneF((prev) => [...prev, ...accepted]);
  }, []);
  const handleIneBDrop = useCallback((_drop, accepted) => {
    setIneB((prev) => [...prev, ...accepted]);
  }, []);
  const handleLicFDrop = useCallback((_drop, accepted) => {
    setLicF((prev) => [...prev, ...accepted]);
  }, []);
  const handleLicBDrop = useCallback((_drop, accepted) => {
    setLicB((prev) => [...prev, ...accepted]);
  }, []);
  const handleOtherDrop = useCallback((_drop, accepted) => {
    setOtherFiles((prev) => [...prev, ...accepted]);
  }, []);

  const isFormValid =
    details.first_names.trim() !== "" &&
    details.father_last_name.trim() !== "" &&
    !curpDuplicate;

  const removeExistingFile = (fileId) => {
    setRemovedFileIds([...removedFileIds, fileId]);
    setExistingFiles(existingFiles.filter((f) => f.id !== fileId));
  };

  const fileTypeLabel = (type) => {
    const labels = {
      ine_front: "INE Frente",
      ine_back: "INE Reverso",
      license_front: "Licencia Frente",
      license_back: "Licencia Reverso",
      other: "Otro",
    };
    return labels[type] || type;
  };

  const save = async () => {
    setIsLoading(true);
    const userId = session?.user?.id;

    const payload = {
      ...details,
      car_category_id: details.car_category_id
        ? parseInt(details.car_category_id)
        : null,
      phones: phones.filter((p) => p.trim() !== ""),
      desired_from: details.desired_from || null,
      desired_to: details.desired_to || null,
    };

    let validacionId = editingId;

    if (editingId) {
      payload.updated_at = new Date().toISOString();
      const { error } = await supabase
        .from("validaciones")
        .update(payload)
        .eq("id", editingId);
      if (error) {
        console.error(error.message);
        alert("Error al actualizar: " + error.message);
        setIsLoading(false);
        return;
      }

      // Log individual field changes
      const fieldLabels = {
        first_names: "Nombre(s)",
        father_last_name: "Apellido Paterno",
        mother_last_name: "Apellido Materno",
        curp: "CURP",
        rfc: "RFC Personal",
        email: "Correo Electrónico",
        story: "Historia / Motivo",
        car_category_id: "Categoría de Vehículo",
        car_segment_text: "Modelo o Tipo Específico",
        company_name: "Empresa",
        company_rfc: "RFC Empresa",
        desired_from: "Fecha Deseada Desde",
        desired_to: "Fecha Deseada Hasta",
      };

      if (originalDetails) {
        for (const key of Object.keys(fieldLabels)) {
          const oldVal = originalDetails[key] || "";
          const newVal = details[key] || "";
          if (oldVal !== newVal) {
            await supabase.from("validacion_activity").insert({
              validacion_id: editingId,
              user_id: userId,
              action: "field_updated",
              content: `Cambió ${fieldLabels[key]}`,
              metadata: { field: key, old_value: oldVal, new_value: newVal },
            });
          }
        }
      }

      // Check phone changes
      const cleanedPhones = phones.filter((p) => p.trim() !== "");
      const oldPhones = originalPhones
        ? originalPhones.filter((p) => p.trim() !== "")
        : [];
      if (JSON.stringify(oldPhones) !== JSON.stringify(cleanedPhones)) {
        await supabase.from("validacion_activity").insert({
          validacion_id: editingId,
          user_id: userId,
          action: "field_updated",
          content: "Cambió Teléfonos",
          metadata: {
            field: "phones",
            old_value: oldPhones.join(", "),
            new_value: cleanedPhones.join(", "),
          },
        });
      }
    } else {
      payload.created_by = userId;
      const { data, error } = await supabase
        .from("validaciones")
        .insert(payload)
        .select("id")
        .single();
      if (error) {
        console.error(error.message);
        alert("Error al crear: " + error.message);
        setIsLoading(false);
        return;
      }
      validacionId = data.id;

      // Log creation activity
      await supabase.from("validacion_activity").insert({
        validacion_id: validacionId,
        user_id: userId,
        action: "created",
        content: "Creó la validación",
      });
    }

    // Handle removed files (editing)
    for (const fileId of removedFileIds) {
      const file = existingFiles.find((f) => f.id === fileId) ||
        (await supabase.from("validacion_files").select("*").eq("id", fileId).single()).data;
      if (file) {
        // Remove from storage
        const path = file.file_url.split("/validacion-docs/")[1];
        if (path) {
          await supabase.storage.from("validacion-docs").remove([path]);
        }
        // Remove DB record
        await supabase.from("validacion_files").delete().eq("id", fileId);
        // Log activity
        await supabase.from("validacion_activity").insert({
          validacion_id: validacionId,
          user_id: userId,
          action: "file_removed",
          content: `Eliminó ${fileTypeLabel(file.file_type)}: ${file.file_name}`,
          metadata: { file_url: file.file_url, file_name: file.file_name, file_type: file.file_type },
        });
      }
    }

    // Upload new files
    const fileGroups = [
      { files: ineF, type: "ine_front", label: "INE Frente" },
      { files: ineB, type: "ine_back", label: "INE Reverso" },
      { files: licF, type: "license_front", label: "Licencia Frente" },
      { files: licB, type: "license_back", label: "Licencia Reverso" },
      { files: otherFiles, type: "other", label: "Archivo" },
    ];

    let uploadErrors = [];

    for (const group of fileGroups) {
      for (const file of group.files) {
        const safeName = file.name
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .replace(/[^a-zA-Z0-9._-]/g, "_");
        const filePath = `${validacionId}/${group.type}_${Date.now()}_${safeName}`;
        const { error: uploadError } = await supabase.storage
          .from("validacion-docs")
          .upload(filePath, file, { upsert: true });

        if (uploadError) {
          console.error("Storage upload error:", uploadError);
          uploadErrors.push(`${file.name}: ${uploadError.message}`);
          continue;
        }

        const { error: insertError } = await supabase
          .from("validacion_files")
          .insert({
            validacion_id: validacionId,
            file_type: group.type,
            file_url: filePath,
            file_name: file.name,
          });

        if (insertError) {
          console.error("File record insert error:", insertError);
          uploadErrors.push(`${file.name}: ${insertError.message}`);
          continue;
        }

        // Log file added activity
        await supabase.from("validacion_activity").insert({
          validacion_id: validacionId,
          user_id: userId,
          action: "file_added",
          content: `Agregó ${group.label}: ${file.name}`,
          metadata: {
            file_url: filePath,
            file_name: file.name,
            file_type: group.type,
          },
        });
      }
    }

    if (uploadErrors.length > 0) {
      alert("Errores al subir archivos:\n" + uploadErrors.join("\n"));
    }

    navigate(`/validaciones/${validacionId}`);
  };

  const dropZoneSection = (label, files, onDrop, accept) => (
    <Card>
      <BlockStack gap="200">
        <Text variant="headingSm">{label}</Text>
        <DropZone onDrop={onDrop} accept={accept} variableHeight>
          <DropZone.FileUpload actionTitle="Subir archivo" />
        </DropZone>
        {files.length > 0 && (
          <BlockStack gap="100">
            {files.map((f, i) => (
              <InlineStack key={i} align="space-between" blockAlign="center">
                <Text variant="bodySm">{f.name}</Text>
                <Button
                  icon={DeleteIcon}
                  variant="plain"
                  tone="critical"
                  onClick={() => {
                    const setter = [setIneF, setIneB, setLicF, setLicB, setOtherFiles][
                      [ineF, ineB, licF, licB, otherFiles].indexOf(files)
                    ];
                    if (setter) setter(files.filter((_, idx) => idx !== i));
                  }}
                />
              </InlineStack>
            ))}
          </BlockStack>
        )}
      </BlockStack>
    </Card>
  );

  return (
    <AppWrapper>
      <Page
        title={editingId ? "Editar Validación" : "Nueva Validación"}
        backAction={{ content: "Validaciones", url: "/validaciones" }}
        primaryAction={
          <Button
            variant="primary"
            onClick={save}
            loading={isLoading}
            disabled={!isFormValid}
          >
            Guardar
          </Button>
        }
      >
        <BlockStack gap="400">
          <FormLayout>
            <FormLayout.Group condensed>
              <TextField
                label="Nombre(s)"
                id="first_names"
                value={details.first_names}
                onChange={handleChange}
                requiredIndicator
              />
              <TextField
                label="Apellido Paterno"
                id="father_last_name"
                value={details.father_last_name}
                onChange={handleChange}
                requiredIndicator
              />
              <TextField
                label="Apellido Materno"
                id="mother_last_name"
                value={details.mother_last_name}
                onChange={handleChange}
              />
            </FormLayout.Group>
            <FormLayout.Group condensed>
              <TextField
                label="CURP"
                id="curp"
                value={details.curp}
                onChange={handleChange}
                onBlur={checkCurpDuplicate}
                error={
                  curpDuplicate
                    ? `Este CURP ya fue validado para ${curpDuplicate.first_names} ${curpDuplicate.father_last_name}`
                    : undefined
                }
                helpText={
                  curpDuplicate ? (
                    <a href={`/validaciones/${curpDuplicate.id}`}>
                      Ver validación existente
                    </a>
                  ) : undefined
                }
              />
              <TextField
                label="RFC Personal"
                id="rfc"
                value={details.rfc}
                onChange={(value, id) => handleChange(value.toUpperCase(), id)}
              />
              <TextField
                label="Correo Electrónico"
                id="email"
                type="email"
                value={details.email}
                onChange={handleChange}
              />
            </FormLayout.Group>
            <FormLayout.Group condensed>
              <TextField
                label="Empresa (opcional)"
                id="company_name"
                value={details.company_name}
                onChange={handleChange}
              />
              <TextField
                label="RFC Empresa"
                id="company_rfc"
                value={details.company_rfc}
                onChange={(value, id) => handleChange(value.toUpperCase(), id)}
              />
            </FormLayout.Group>
          </FormLayout>

          {/* Phones */}
          <Card>
            <BlockStack gap="200">
              <Text variant="headingSm">Teléfonos</Text>
              {phones.map((phone, index) => (
                <InlineStack key={index} gap="200" blockAlign="end">
                  <div style={{ flexGrow: 1 }}>
                    <TextField
                      label={`Teléfono ${index + 1}`}
                      value={phone}
                      onChange={(val) => handlePhoneChange(val, index)}
                      type="tel"
                    />
                  </div>
                  {phones.length > 1 && (
                    <Button
                      icon={DeleteIcon}
                      variant="plain"
                      tone="critical"
                      onClick={() => removePhone(index)}
                    />
                  )}
                </InlineStack>
              ))}
              <div>
                <Button icon={PlusIcon} onClick={addPhone}>
                  Agregar Teléfono
                </Button>
              </div>
            </BlockStack>
          </Card>

          {/* Car segment */}
          <FormLayout>
            <FormLayout.Group condensed>
              <Select
                label="Categoría de Vehículo"
                id="car_category_id"
                options={categories}
                value={details.car_category_id}
                onChange={(value, id) => handleChange(value, id)}
              />
              <TextField
                label="Modelo o Tipo Específico"
                id="car_segment_text"
                value={details.car_segment_text}
                onChange={handleChange}
                placeholder="Ej: Suburban, Versa, Pickup"
              />
            </FormLayout.Group>
          </FormLayout>

          {/* Desired rental dates */}
          <FormLayout>
            <FormLayout.Group condensed>
              <TextField
                label="Fecha Deseada Desde"
                id="desired_from"
                type="date"
                value={details.desired_from}
                onChange={handleChange}
              />
              <TextField
                label="Fecha Deseada Hasta (opcional)"
                id="desired_to"
                type="date"
                value={details.desired_to}
                onChange={handleChange}
              />
            </FormLayout.Group>
          </FormLayout>

          {/* Story */}
          <FormLayout>
            <TextField
              label="Historia / Motivo de Renta"
              id="story"
              value={details.story}
              onChange={handleChange}
              multiline={4}
            />
          </FormLayout>

          {/* Existing files (edit mode) */}
          {existingFiles.length > 0 && (
            <Card>
              <BlockStack gap="200">
                <Text variant="headingSm">Archivos Existentes</Text>
                {existingFiles.map((file) => (
                  <InlineStack
                    key={file.id}
                    align="space-between"
                    blockAlign="center"
                  >
                    <InlineStack gap="200" blockAlign="center">
                      <Text variant="bodySm" tone="subdued">
                        {fileTypeLabel(file.file_type)}
                      </Text>
                      <Text variant="bodySm">{file.file_name}</Text>
                    </InlineStack>
                    <Button
                      icon={DeleteIcon}
                      variant="plain"
                      tone="critical"
                      onClick={() => removeExistingFile(file.id)}
                    />
                  </InlineStack>
                ))}
              </BlockStack>
            </Card>
          )}

          {/* File uploads */}
          <Text variant="headingMd">Documentos</Text>
          <div className="validacion-file-grid">
            {dropZoneSection("INE Frente", ineF, handleIneFDrop, "image/*")}
            {dropZoneSection("INE Reverso", ineB, handleIneBDrop, "image/*")}
            {dropZoneSection("Licencia Frente", licF, handleLicFDrop, "image/*")}
            {dropZoneSection("Licencia Reverso", licB, handleLicBDrop, "image/*")}
          </div>
          {dropZoneSection("Otros Archivos", otherFiles, handleOtherDrop)}
        </BlockStack>
      </Page>
    </AppWrapper>
  );
}

export default NewValidacion;
